import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import useElevenLabsTTS from '@/hooks/useElevenLabsTTS';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowLeft, Trophy, Star, BookOpen, Calculator, 
  FlaskConical, Pencil, Play, ClipboardCheck, Mic, Volume2,
  Loader2, Lock, Heart, Clock, Gift, Users, Gamepad2, Book, ChevronRight,
  Globe, Shield, Award, Rocket, Languages, Puzzle, Music2, Sun,
  Landmark, Palette, Target, Activity, Wand2, Brain, Search
} from 'lucide-react';
import { DailyChallengeCard } from '@/components/DailyChallengeCard';
import AssignedLessonsStrip from '@/components/lessons/AssignedLessonsStrip';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const LearningHub = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [child, setChild] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [bibleEnabled, setBibleEnabled] = useState(false);
  const { speak: speakPreK } = useElevenLabsTTS({ voice: 'shimmer', speed: 0.95 });

  // Fetch whether the parent has opted-in to the Bible curriculum. Off by default.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/user/curriculum-settings');
        setBibleEnabled(!!res.data?.bible_curriculum_enabled);
      } catch (err) { setBibleEnabled(false); }
    })();
  }, [api]);

  // Detect Pre-K (age <= 5) once we have the child
  const isPreK = React.useMemo(() => {
    if (!child) return false;
    if (child.birth_date) {
      try {
        const dt = new Date(child.birth_date);
        const now = new Date();
        const months = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth());
        return months < 72;
      } catch (e) { /* ignore */ }
    }
    return ['infant','toddler','preschool'].includes(String(child.age_group || '').toLowerCase());
  }, [child]);

  // Auto-welcome Pre-K children when the hub loads. Reads their name and a short prompt.
  useEffect(() => {
    if (!isPreK || !child) return;
    const line = `Hi ${child.name || 'friend'}. Tap a big picture to start playing and learning.`;
    const t = setTimeout(() => speakPreK(line), 700);
    return () => clearTimeout(t);
  }, [isPreK, child]);

  // Define which subjects are appropriate for each age group
  // Define which subjects are appropriate for each age group.
  // Music module is temporarily removed; Bible/Christian is opt-in via parent settings.
  const AGE_GROUP_SUBJECTS = {
    infant: ['alphabets'], // Very limited - mostly sensory
    toddler: ['alphabets', 'counting'],
    preschool: ['alphabets', 'counting', 'reading', 'writing'],
    early_elementary: ['alphabets', 'counting', 'reading', 'writing', 'math', 'science', 'summer_slide'],
    elementary: ['reading', 'writing', 'math', 'science', 'summer_slide'],
    middle: ['reading', 'writing', 'math', 'science', 'summer_slide']
  };
  // Bible is inserted only when the parent has opted in. Handled at filter time below.

  const allSubjects = [
    { key: 'alphabets', name: 'Alphabets', icon: BookOpen, color: '#10B981', bgClass: 'subject-alphabets' },
    { key: 'counting', name: 'Counting', icon: Calculator, color: '#F59E0B', bgClass: 'subject-counting' },
    { key: 'reading', name: 'Reading', icon: BookOpen, color: '#10B981', bgClass: 'subject-reading' },
    { key: 'writing', name: 'Writing', icon: Pencil, color: '#06B6D4', bgClass: 'subject-writing' },
    { key: 'math', name: 'Math', icon: Calculator, color: '#EF4444', bgClass: 'subject-math' },
    { key: 'science', name: 'Science', icon: FlaskConical, color: '#8B5CF6', bgClass: 'subject-science' },
    { key: 'christian', name: 'Bible & Faith', icon: Heart, color: '#EC4899', bgClass: 'subject-christian' },
    { key: 'music', name: 'Music', icon: Music2, color: '#A855F7', bgClass: 'subject-music' },
    { key: 'summer_slide', name: 'Summer Slide', icon: Sun, color: '#F97316', bgClass: 'subject-summer' },
  ];

  // Filter subjects based on child's age group + Bible opt-in flag
  const getAvailableSubjects = () => {
    const allowedKeys = [...(AGE_GROUP_SUBJECTS[child?.age_group] || AGE_GROUP_SUBJECTS.preschool)];
    if (bibleEnabled) allowedKeys.push('christian');
    // Music module intentionally excluded (temporarily disabled at product level)
    return allSubjects.filter((s) => s.key !== 'music' && allowedKeys.includes(s.key));
  };
  // Always use the filtered list — even before `child` loads — so we never
  // flash the Music or Bible tiles for a split second while data is fetching.
  const subjects = getAvailableSubjects();


  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, curriculumRes, resumeRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/curriculum/${childId}`),
        api.get(`/children/${childId}/resume`).catch(() => ({ data: { type: null } })),
      ]);
      setChild(childRes.data);
      setCurriculum(curriculumRes.data);
      setResumeTarget(resumeRes.data?.type ? resumeRes.data : null);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load learning hub');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSpeak = async (text) => {
    if (!child?.accessibility_settings?.tts_enabled) return;
    
    try {
      // Try browser TTS first
      _speakTTS(text, 0.9);
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const getActivityCount = (subject) => {
    return curriculum?.curriculum?.[subject]?.length || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-100 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white theme-kids">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3 shrink-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                  {child?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-fredoka font-bold text-slate-800 leading-tight">{child?.name}</p>
                  <p className="text-xs text-slate-500">Level {child?.current_level}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible md:flex-wrap md:justify-end -mx-1 px-1 pb-1 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* wrap buttons with shrink-0 so they don't get squeezed */}
              {/* Show Stories for toddler+ */}
              {child?.age_group !== 'infant' && (
                <Link to={`/stories/${childId}`}>
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="stories-link">
                    <Book className="w-4 h-4 mr-1 text-orange-500" />
                    Stories
                  </Button>
                </Link>
              )}
              {/* Show Games for toddler+ */}
              {child?.age_group !== 'infant' && (
                <Link to={`/games/${childId}?subject=alphabets`}>
                  <Button variant="outline" size="sm" className="rounded-full" data-testid="games-link">
                    <Gamepad2 className="w-4 h-4 mr-1 text-green-500" />
                    Games
                  </Button>
                </Link>
              )}
              {/* Show Multiplayer/Together for preschool+ */}
              {['preschool', 'early_elementary', 'elementary', 'middle'].includes(child?.age_group) && (
                <>
                  <Link to={`/multiplayer/${childId}`}>
                    <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="multiplayer-link">
                      <Users className="w-4 h-4 mr-1 text-purple-500" />
                      VS
                    </Button>
                  </Link>
                  <Link to={`/collaborative/${childId}`}>
                    <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="collaborative-link">
                      <Heart className="w-4 h-4 mr-1 text-pink-500" />
                      Together
                    </Button>
                  </Link>
                </>
              )}
              {/* Show Challenges for preschool+ */}
              {['preschool', 'early_elementary', 'elementary', 'middle'].includes(child?.age_group) && (
                <Link to={`/challenges/${childId}`}>
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="challenges-link">
                    <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
                    Challenges
                  </Button>
                </Link>
              )}
              {/* Show Badges for toddler+ */}
              {child?.age_group !== 'infant' && (
                <Link to={`/badges/${childId}`}>
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="badges-link">
                    <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
                    {child?.badges?.length || 0}
                  </Button>
                </Link>
              )}
              {/* Show Rewards for toddler+ */}
              {child?.age_group !== 'infant' && (
                <Link to={`/rewards/${childId}`}>
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="rewards-link">
                    <Gift className="w-4 h-4 mr-1 text-amber-500" />
                    {child?.coins || 0}
                  </Button>
                </Link>
              )}
              <Link to={`/time/${childId}`}>
                <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="time-link">
                  <Clock className="w-4 h-4 mr-1 text-green-500" />
                </Button>
              </Link>
              <Link to="/family">
                <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="family-mode-link">
                  <Users className="w-4 h-4 text-purple-500" />
                </Button>
              </Link>
              <div className="flex items-center gap-1 bg-emerald-100 rounded-full px-3 py-1 shrink-0">
                <Star className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-bold text-emerald-700">{child?.total_xp || 0} XP</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-emerald-100 py-2 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">Level {child?.current_level}</span>
            <Progress value={((child?.total_xp || 0) % 500) / 5} className="flex-1 h-3" />
            <span className="text-sm text-slate-500">{500 - ((child?.total_xp || 0) % 500)} XP to next level</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="font-fredoka text-4xl font-bold text-slate-800 mb-2">
            Let's Learn, {child?.name}! 
          </h1>
          <p className="text-lg text-slate-600">Choose a subject to start your adventure</p>
          
          {child?.accessibility_settings?.tts_enabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSpeak(`Let's learn ${child?.name}! Choose a subject to start your adventure`)}
              className="mt-2"
              data-testid="tts-welcome-btn"
            >
              <Volume2 className="w-4 h-4 mr-1" /> Listen
            </Button>
          )}
        </motion.div>

        {/* Early Learning Banner - for younger children */}
        {['infant', 'toddler', 'preschool', 'early_elementary'].includes(child?.age_group) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <Link to={`/early-learning/${childId}`} data-testid="early-learning-banner">
              <Card className="bg-gradient-to-r from-amber-400 via-orange-400 to-pink-400 text-white hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl">
                      🎨
                    </div>
                    <div>
                      <h3 className="font-fredoka text-xl font-bold">Early Learning</h3>
                      <p className="text-amber-100">ABCs, 123s, Phonics & ABC Song - Tap to hear letters speak!</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔊</span>
                    <ChevronRight className="w-8 h-8" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        )}

        {/* Picture Dictionary — visual glossary from AI illustrations */}
        {/* Reading Assessment CTA — surfaces for parents/kids who never open the parent dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid gap-4 md:grid-cols-3 mb-8"
        >
          <Link to={`/assessment/${childId}/reading`} data-testid="reading-assessment-banner">
            <Card className="h-full bg-gradient-to-br from-indigo-400 via-blue-400 to-cyan-400 text-white hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer">
              <CardContent className="p-5">
                <div className="text-3xl mb-2">📊</div>
                <h3 className="font-fredoka text-lg font-bold mb-1">Reading Check-in</h3>
                <p className="text-blue-50 text-sm">2-minute Lexile assessment.</p>
              </CardContent>
            </Card>
          </Link>

          <Link to={`/assessment/${childId}/reading?practice=1&limit=3`} data-testid="practice-test-banner">
            <Card className="h-full bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 text-white hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer">
              <CardContent className="p-5">
                <div className="text-3xl mb-2">⚡</div>
                <h3 className="font-fredoka text-lg font-bold mb-1">Take a Practice Test</h3>
                <p className="text-amber-50 text-sm">3 quick questions — updates your Lexile.</p>
              </CardContent>
            </Card>
          </Link>

          <Link to={`/study-history/${childId}`} data-testid="study-history-banner">
            <Card className="h-full bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-400 text-white hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer">
              <CardContent className="p-5">
                <div className="text-3xl mb-2">📚</div>
                <h3 className="font-fredoka text-lg font-bold mb-1">What I've Studied</h3>
                <p className="text-emerald-50 text-sm">See every activity you've completed.</p>
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Link to={`/picture-dictionary/${childId}`} data-testid="picture-dictionary-banner">
            <Card className="bg-gradient-to-r from-sky-400 via-cyan-400 to-emerald-400 text-white hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl">
                    📖
                  </div>
                  <div>
                    <h3 className="font-fredoka text-xl font-bold">Picture Dictionary</h3>
                    <p className="text-cyan-50">A picture book of everything you&apos;re learning — tap to hear each one</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔊</span>
                  <ChevronRight className="w-8 h-8" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Being Polite & Kind - Social Emotional Learning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mb-8"
        >
          <Link to={`/sel/${childId}`} data-testid="sel-banner">
            <Card className="bg-gradient-to-r from-rose-400 via-pink-400 to-amber-300 text-white hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl">
                    💖
                  </div>
                  <div>
                    <h3 className="font-fredoka text-xl font-bold">Being Polite &amp; Kind</h3>
                    <p className="text-rose-50">Learn the magic words: please, thank you, sharing &amp; kindness</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🙏</span>
                  <ChevronRight className="w-8 h-8" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Continue Where I Left Off — Hero Card */}
        {resumeTarget && resumeTarget.type && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
            data-testid="resume-hero-card"
          >
            <Link to={resumeTarget.url}>
              <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-teal-50 hover:shadow-lg transition-all cursor-pointer overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                          Continue where you left off
                        </span>
                      </div>
                      <h3 className="font-fredoka text-xl font-bold text-slate-800 truncate">
                        {resumeTarget.title}
                      </h3>
                      <p className="text-sm text-slate-600 truncate">{resumeTarget.subtitle}</p>
                    </div>
                    <div className="hidden md:flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-semibold whitespace-nowrap">
                      {resumeTarget.cta}
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        )}

        {/* Daily Writing Prompt Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Link to={`/daily-writing/${childId}`} data-testid="daily-writing-banner">
            <Card className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Pencil className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-fredoka text-xl font-bold">Daily Writing Prompt</h3>
                    <p className="text-cyan-100">Practice your writing skills with today's creative prompt!</p>
                  </div>
                </div>
                <ChevronRight className="w-8 h-8" />
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Daily Challenge */}
        <AssignedLessonsStrip childId={childId} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="mb-8"
        >
          <DailyChallengeCard childId={childId} apiHelper={api} theme="light" excludeBible={!bibleEnabled} />
        </motion.div>

        {/* Word Puzzles Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <Link to={`/word-puzzles/${childId}`} data-testid="word-puzzles-banner">
            <Card className="bg-gradient-to-r from-fuchsia-500 via-purple-500 to-indigo-500 text-white hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Puzzle className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-fredoka text-xl font-bold flex items-center gap-2">
                      Word Puzzles
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">NEW</span>
                    </h3>
                    <p className="text-purple-100">Scrambles, Word Search, Crosswords & Missing Letters!</p>
                  </div>
                </div>
                <ChevronRight className="w-8 h-8" />
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* AI-Powered Adaptive Curriculum Banner */}
        {['preschool', 'early_elementary', 'elementary', 'middle'].includes(child?.age_group) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:shadow-lg transition-all overflow-hidden">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="font-fredoka text-xl font-bold flex items-center gap-2">
                        AI-Powered Adaptive Learning
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">NEW</span>
                      </h3>
                      <p className="text-purple-100 text-sm">Personalized lessons across Reading, Math, Science, History &amp; Arts</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/ai-curriculum/${childId}/reading`} data-testid="ai-reading-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <BookOpen className="w-4 h-4 mr-2" /> Reading
                      </Button>
                    </Link>
                    <Link to={`/ai-curriculum/${childId}/math`} data-testid="ai-math-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <Calculator className="w-4 h-4 mr-2" /> Math
                      </Button>
                    </Link>
                    <Link to={`/ai-curriculum/${childId}/science`} data-testid="ai-science-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <FlaskConical className="w-4 h-4 mr-2" /> Science
                      </Button>
                    </Link>
                    <Link to={`/ai-curriculum/${childId}/history`} data-testid="ai-history-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <Landmark className="w-4 h-4 mr-2" /> History
                      </Button>
                    </Link>
                    <Link to={`/ai-curriculum/${childId}/arts`} data-testid="ai-arts-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <Palette className="w-4 h-4 mr-2" /> Arts
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Kiddom Math Curriculum Banner */}
        {['early_elementary', 'elementary', 'middle'].includes(child?.age_group) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            className="mb-8"
          >
            <Link to={`/kiddom-math/${childId}`} data-testid="kiddom-math-banner">
              <Card className="bg-gradient-to-r from-red-500 via-orange-500 to-amber-500 text-white hover:shadow-lg transition-all overflow-hidden cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Calculator className="w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="font-fredoka text-xl font-bold flex items-center gap-2">
                          Kiddom Math Curriculum
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">CCSS K-8</span>
                        </h3>
                        <p className="text-orange-50">Sequenced Common Core scope &amp; sequence from Kindergarten to Grade 8</p>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 hidden md:block" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        )}

        {/* Pre-K banner — audio-first, no reading needed */}
        {['infant', 'toddler', 'preschool'].includes(child?.age_group) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <Card className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white overflow-hidden" data-testid="prek-banner">
              <CardContent className="p-5 flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <h3 className="font-fredoka text-lg font-bold">Little Learner Mode</h3>
                  <p className="text-orange-50 text-sm">Audio-first, tap-and-listen play — no reading required. Every lesson is spoken to your child.</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Skills tools — Skill Map, Benchmarks, Materials Generator */}
        {['early_elementary', 'elementary', 'middle', 'preschool'].includes(child?.age_group) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
            className="mb-8 grid md:grid-cols-5 gap-3"
          >
            <Link to={`/skill-map/${childId}`} data-testid="skill-map-tile">
              <Card className="border-2 border-orange-200 hover:border-orange-400 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Skill Map</p>
                    <p className="text-xs text-slate-500">Master skills, unlock the next</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/benchmarks/${childId}`} data-testid="benchmarks-tile">
              <Card className="border-2 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Reading Benchmarks</p>
                    <p className="text-xs text-slate-500">Acadience-style progress</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/materials`} data-testid="materials-tile">
              <Card className="border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Wand2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Generate Materials</p>
                    <p className="text-xs text-slate-500">AI worksheets & flashcards</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/library/${childId}`} data-testid="library-tile">
              <Card className="border-2 border-rose-200 hover:border-rose-400 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">My Library</p>
                    <p className="text-xs text-slate-500">Liked stories & lessons</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/critical-thinking/${childId}`} data-testid="wonder-room-tile">
              <Card className="border-2 border-fuchsia-200 hover:border-fuchsia-400 hover:shadow-md transition-all cursor-pointer h-full bg-gradient-to-br from-fuchsia-50 to-indigo-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-fuchsia-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Wonder Room</p>
                    <p className="text-xs text-slate-500">Curiosity questions &amp; log</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/lessons/search/${childId}`} data-testid="find-lesson-tile">
              <Card className="border-2 border-amber-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer h-full bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Search className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Find a Lesson</p>
                    <p className="text-xs text-slate-500">Search any topic, or make a new one</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/explore/${childId}`} data-testid="explore-learn-tile">
              <Card className="border-2 border-teal-200 hover:border-teal-400 hover:shadow-md transition-all cursor-pointer h-full bg-gradient-to-br from-teal-50 to-cyan-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Explore &amp; Learn</p>
                    <p className="text-xs text-slate-500">Pick any topic — taught at your grade</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/trace/${childId}`} data-testid="trace-writing-tile">
              <Card className="border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer h-full bg-gradient-to-br from-indigo-50 to-violet-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center">
                    <Pencil className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Trace &amp; Write</p>
                    <p className="text-xs text-slate-500">Letters A–Z &amp; numbers 0–9</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            {bibleEnabled && (
              <Link to={`/bible-journey/${childId}`} data-testid="bible-journey-tile">
                <Card className="border-2 border-amber-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">Bible Journey</p>
                      <p className="text-xs text-slate-500">90-day guided reading plan</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
        )}

        {/* Language Learning Banner - Temporarily hidden per product decision */}
        {false && ['preschool', 'early_elementary', 'elementary', 'middle'].includes(child?.age_group) && (          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:shadow-lg transition-all overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                      <Globe className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="font-fredoka text-xl font-bold flex items-center gap-2">
                        Language Learning
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">NEW</span>
                      </h3>
                      <p className="text-pink-100">Learn Spanish, French, or Akan!</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/language/${childId}/spanish`} data-testid="spanish-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        🇪🇸 Spanish
                      </Button>
                    </Link>
                    <Link to={`/language/${childId}/french`} data-testid="french-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        🇫🇷 French
                      </Button>
                    </Link>
                    <Link to={`/language/${childId}/akan`} data-testid="akan-link">
                      <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                        🇬🇭 Akan
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Quick Actions Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {/* Initial Assessment */}
          <Link to={`/assessment/${childId}/reading`} data-testid="ela-assessment-link">
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Rocket className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-semibold text-emerald-800 text-sm">ELA Assessment</h4>
                <p className="text-xs text-emerald-600">Find your level</p>
              </CardContent>
            </Card>
          </Link>

          {/* Math Assessment */}
          <Link to={`/assessment/${childId}/math`} data-testid="math-assessment-link">
            <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Calculator className="w-6 h-6 text-red-600" />
                </div>
                <h4 className="font-semibold text-red-800 text-sm">Math Assessment</h4>
                <p className="text-xs text-red-600">Find your level</p>
              </CardContent>
            </Card>
          </Link>

          {/* Safety Games */}
          <Link to={`/digital-safety-games/${childId}`} data-testid="safety-games-link">
            <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Shield className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="font-semibold text-cyan-800 text-sm">Safety Games</h4>
                <p className="text-xs text-cyan-600">Play & Learn</p>
              </CardContent>
            </Card>
          </Link>

          {/* Writing Badges */}
          <Link to={`/writing-badges/${childId}`} data-testid="writing-badges-link">
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-semibold text-amber-800 text-sm">Writing Badges</h4>
                <p className="text-xs text-amber-600">Earn rewards</p>
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Subjects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {subjects.map((subject, index) => (
            <motion.div
              key={subject.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                className={`kids-card border-4 ${subject.bgClass} cursor-pointer`}
                data-testid={`subject-card-${subject.key}`}
              >
                <CardContent className="p-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${subject.color}20` }}
                  >
                    <subject.icon className="w-8 h-8" style={{ color: subject.color }} />
                  </div>
                  
                  <h3 className="font-fredoka text-2xl font-bold text-slate-800 mb-1">
                    {subject.name}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {getActivityCount(subject.key)} activities available
                  </p>

                  <div className="flex gap-2">
                    <Link 
                      to={
                        subject.key === 'christian' 
                          ? `/bible-stories/${childId}`
                          : subject.key === 'music'
                            ? `/music/${childId}`
                            : subject.key === 'summer_slide'
                              ? `/summer-slide/${childId}`
                              : ['reading', 'math', 'writing', 'science'].includes(subject.key)
                                ? `/adaptive/${childId}/${subject.key}`
                                : `/activity/${subject.key}-1?child=${childId}`
                      } 
                      className="flex-1"
                      onClick={() => handleSpeak(`Starting ${subject.name}`)}
                    >
                      <Button 
                        className="w-full kids-button"
                        style={{ backgroundColor: subject.color }}
                        data-testid={`play-${subject.key}`}
                      >
                        <Play className="w-5 h-5 mr-2" /> Play
                      </Button>
                    </Link>
                    <Link to={`/assessment/${childId}/${subject.key}`}>
                      <Button 
                        variant="outline" 
                        className="h-16 px-4"
                        data-testid={`quiz-${subject.key}`}
                      >
                        <ClipboardCheck className="w-5 h-5" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Voice & TTS Info */}
        {(child?.accessibility_settings?.voice_enabled || child?.accessibility_settings?.tts_enabled) && (
          <Card className="bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
                  {child?.accessibility_settings?.voice_enabled ? (
                    <Mic className="w-6 h-6 text-sky-500" />
                  ) : (
                    <Volume2 className="w-6 h-6 text-sky-500" />
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">
                    {child?.accessibility_settings?.voice_enabled && child?.accessibility_settings?.tts_enabled
                      ? 'Voice & Audio Enabled'
                      : child?.accessibility_settings?.voice_enabled
                        ? 'Voice Input Enabled'
                        : 'Text-to-Speech Enabled'}
                  </h4>
                  <p className="text-sm text-slate-600">
                    {child?.accessibility_settings?.voice_enabled && 'You can speak your answers! '}
                    {child?.accessibility_settings?.tts_enabled && 'Click the speaker icon to hear instructions.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default LearningHub;
